<template>
  <v-card class="gallery-view gallery-view--insect-v2">
    <gallery-view-toolbar
      :editable="editable"
      :event="event"
      :sensorType="sensorType"
      :canDownload="canDownload"
      :title="title"
      @download="download"
      @update="(attr) => $emit('updateEvent', attr)"
      @close="$emit('close')"
    />
    <layout
      :hasPrevious="hasPrevious"
      :hasNext="hasNext"
      @previous="$emit('previous')"
      @next="$emit('next')"
    >
      <template #top>
        <gallery-view-header
          :editable="editable"
          :event="event"
          @updateEvent="$emit('updateEvent', $event)"
        />
      </template>
      <div>
        <v-carousel v-model="index" touchless :height="null">
          <v-carousel-item v-for="image in images" :key="image.id" eager>
            <picked-insects-draw
              :imageSrc="event.attachments[image]"
              :pickedDetectionType="pickedDetectionType"
              :currentPhotoKey="editTarget"
              :event="event"
              :hideCanvas="hideCanvas"
            >
              <loading-image
                :src="event.attachments[image]"
                :forceLoading="imageLoading"
              />
            </picked-insects-draw>
          </v-carousel-item>
        </v-carousel>
        <slot name="thumbnails" />
      </div>
      <template #side>
        <div class="result-table pa-2">
          <v-checkbox
            class="pa-0 mb-3"
            :label="$t('field.original')"
            v-model="viewOriginal"
            hide-details
            color="primary"
          />
          <insect-count-table
            :insectCounts="event.insect_counts"
            :showRadiobutton="canPickDetectionType && !imageLoading"
            :pickedDetectionType.sync="pickedDetectionType"
          />
        </div>
      </template>
    </layout>
  </v-card>
</template>

<i18n lang="yaml">
ja:
  field:
    original: 撮影画像を表示

en:
  field:
    original: Show original photo
</i18n>

<script>
import { LATEST_VERSION } from '@/libs/insectTypes';

import PickedInsectsDraw from '@/components/_legacy/PickedInsectsDraw';
import InsectCountTable from '@/components/InsectCountTable';
import LoadingImage from '@/components/LoadingImage';
import GalleryViewHeader from '@/components/GalleryView/GalleryViewHeader';
import GalleryViewToolbar from '@/components/GalleryView/Toolbar';
import Layout from '@/components/GalleryView/Layout';

export default {
  name: 'InsectViewV2',
  props: {
    editable: Boolean,
    event: Object,
    sensorType: String,
    hasPrevious: Boolean,
    hasNext: Boolean,
    title: String,
  },
  components: {
    InsectCountTable,
    GalleryViewHeader,
    GalleryViewToolbar,
    Layout,
    LoadingImage,
    PickedInsectsDraw,
  },
  computed: {
    canPickDetectionType() {
      const isImageSrc = !!this.event.attachments[this.currentImage];
      return this.viewMode !== 'original' && isImageSrc && (this.dataVersion === LATEST_VERSION);
    },
    canDownload() {
      return !this.event.updating && !!this.event.attachments[this.currentImage];
    },
    currentImage() {
      return this.images[this.index];
    },
    editTarget() {
      return this.currentImage.replace('detections-', '');
    },
    dataVersion() {
      return this.event.insect_types_version;
    },
    hideCanvas() {
      return this.viewMode === 'original';
    },
    images() {
      return ['photo_left', 'photo_center', 'photo_right'];
    },
    imageLoading() {
      return this.viewMode !== 'original' && this.event.updating;
    },
    insectCounts() {
      return this.event.insect_counts;
    },
  },
  data() {
    return {
      index: 0,
      viewMode: 'detections',
      pickedDetectionType: 'all',
      viewOriginal: false,
    };
  },
  inject: ['downloadAttachment'],
  methods: {
    download: async function () {
      const resource = (this.viewMode === 'original' ? this.currentImage : `detections-${this.currentImage}`);
      this.downloadAttachment(this.event.id, resource);
    },
  },
  watch: {
    event(newValue, oldValue) {
      if (newValue.id !== oldValue.id) {
        this.viewMode = 'detections';
        this.viewOriginal = false;
        this.pickedDetectionType = 'all';
      }
    },
    viewOriginal(value) {
      if (value) {
        this.viewMode = 'original';
        this.pickedDetectionType = 'all';
      } else {
        this.viewMode = 'detections';
      }
    },
  },
};
</script>

<style scoped lang="sass">
.result-table
  border: 2px solid #ccc

  ::v-deep th.column
    padding: 0 12px

.v-carousel
  height: calc(65vh + 50px)
  padding-bottom: 50px
  box-sizing: content-box

  ::v-deep &__item,
  ::v-deep .v-responsive__content,
  .loading-image
    height: 100%

  ::v-deep &__controls
    background-color: transparent

    &__item
      color: #999 !important
</style>
